/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Else, If, Then, Unless, When } from "react-if";
import {
  Button,
  Description,
  FlexContainer,
  Spacer,
  Spinner,
  Text,
  theme,
} from "@nordcloud/gnui";
import { EditButton } from "~/components/Buttons";
import { InlineContacts } from "~/components/InlineContacts";
import { isEmpty } from "~/tools";

type Props = {
  initValues: {
    id: string;
    name: string;
  }[];
  property: string;
  readOnly?: boolean;
  required?: boolean;
  onSubmit: SubmitHandler<UpdateContactPersonInput>;
};

export function InlineContactPicker({
  initValues,
  property,
  readOnly = false,
  required = true,
  onSubmit,
}: Props) {
  const { handleSubmit: handleFormSubmit, setValue, register } = useForm();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const submitContactPersons = async (data: UpdateContactPersonInput) => {
    setIsEditMode(false);
    setIsLoading(true);
    await onSubmit(data);
    setIsLoading(false);
  };

  const [contacts, setContacts] = useState<string[]>([]);

  const handleAdd = (newPerson: string) => {
    const newContacts = [...contacts, newPerson];
    setContacts(newContacts);
    setValue(property, newContacts);
  };

  const handleRemove = (deletedPerson: string) => {
    const newContacts = contacts.filter((item) => item !== deletedPerson);
    setContacts(newContacts);
    setValue(property, newContacts);
  };

  const defaultContactIds = initValues.map(({ id }) => id);
  const defaultContactNames = initValues.map(({ name }) => name);

  const EditForm = (
    <form
      css={{ display: "block" }}
      onSubmit={handleFormSubmit(submitContactPersons)}
    >
      <input
        type="hidden"
        {...register(property, { required })}
        defaultValue={defaultContactIds}
      />
      <FlexContainer>
        <InlineContacts
          contacts={contacts}
          defaultNames={defaultContactNames}
          onAdd={handleAdd}
          onRemove={handleRemove}
        />
        <Button
          ml={theme.spacing.spacing02}
          type="submit"
          icon="save"
          size="md"
          severity="low"
        >
          Save
        </Button>
        <Button
          ml={theme.spacing.spacing02}
          icon="close"
          type="button"
          size="md"
          severity="low"
          onClick={() => {
            setIsEditMode(false);
          }}
        >
          Cancel
        </Button>
      </FlexContainer>
      <When condition={isEmpty(contacts) && required}>
        <Description>At least one contact person is required.</Description>
      </When>
    </form>
  );

  return (
    <LoadingWrapper isLoading={isLoading}>
      <If condition={isEditMode}>
        <Then>{EditForm}</Then>
        <Else>
          <FlexContainer alignItems="center">
            <DisplayText
              content={initValues.map(({ name }) => name).join(", ")}
              readOnly={readOnly}
              onChange={() => {
                setIsEditMode(true);
                setContacts(defaultContactIds);
              }}
            />
          </FlexContainer>
          <Spacer height={theme.spacing.spacing04} />
        </Else>
      </If>
    </LoadingWrapper>
  );
}

type UpdateContactPersonInput = {
  contactPersonIds?: string[];
};

type DisplayTextProps = {
  content: string;
  readOnly: boolean;
  onChange: () => void;
};

function DisplayText({ content, readOnly, onChange }: DisplayTextProps) {
  return (
    <>
      <Text as="div" m="0" mr={theme.spacing.spacing02}>
        {content}
      </Text>
      <Unless condition={readOnly}>
        <EditButton onClick={onChange} />
      </Unless>
    </>
  );
}

type LoadingWrapperProps = {
  isLoading: boolean;
  children: ReactNode;
};

function LoadingWrapper({ isLoading, children }: LoadingWrapperProps) {
  return (
    <If condition={isLoading}>
      <Then>
        <FlexContainer>
          <Spinner size="sm" />
          <Text tag="div" ml={theme.spacing.spacing04}>
            Loading...
          </Text>
        </FlexContainer>
        <Spacer height={theme.spacing.spacing04} />
      </Then>
      <Else>{children}</Else>
    </If>
  );
}
